import { useEffect, useState } from "react";
import { useForm, useFieldArray } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { profileFormSchema } from "../../schemas/profileFormSchema";
import Loader from "../../utils/Loader";
import { useProfile } from "../../components/portfolio/context/ProfileContext";
import "../../assets/css/admin/profileEditor.css";
import fallbackPortfolioDB from "../../data/portfolioDB.json";

const apiBase = process.env.REACT_APP_BACKEND_API_URL;

const fbIntro = fallbackPortfolioDB.intro || {};

function defaultIntroForm() {
  return {
    greeting: fbIntro.greeting || "",
    rotatingTitles: fbIntro.rotatingTitles?.length ? [...fbIntro.rotatingTitles] : [""],
    bio: fbIntro.bio || "",
    imageSrc: fbIntro.imageSrc || "images/me.jpg",
  };
}

function mapApiToForm(payload) {
  if (!payload) {
    return { intro: defaultIntroForm(), expDetails: [], eduDetails: [] };
  }
  const introIn = payload.intro && typeof payload.intro === "object" ? payload.intro : null;
  return {
    intro: {
      greeting: introIn?.greeting ?? fbIntro.greeting ?? "",
      rotatingTitles:
        Array.isArray(introIn?.rotatingTitles) && introIn.rotatingTitles.length
          ? introIn.rotatingTitles
          : defaultIntroForm().rotatingTitles,
      bio: introIn?.bio ?? fbIntro.bio ?? "",
      imageSrc: introIn?.imageSrc ?? fbIntro.imageSrc ?? "images/me.jpg",
    },
    expDetails: (payload.expDetails || []).map((e) => ({
      id: e.id,
      title: e.title ?? "",
      subtitle: e.subtitle ?? "",
      year: e.year ?? "",
      src: e.src ?? "",
      listItems: Array.isArray(e.listItems) && e.listItems.length ? e.listItems : [""],
    })),
    eduDetails: (payload.eduDetails || []).map((e) => ({
      id: e.id,
      title: e.title ?? "",
      subtitle: e.subtitle ?? "",
      year: e.year ?? "",
      src: e.src ?? "",
    })),
  };
}

function IntroRotatingLines({ control, register }) {
  const { fields, append, remove } = useFieldArray({
    control,
    name: "intro.rotatingTitles",
  });

  return (
    <div className="profile-editor__field">
      <span className="profile-editor__label">Rotating headline lines (typewriter)</span>
      {fields.map((field, i) => (
        <div key={field.id} style={{ display: "flex", gap: 8, marginBottom: 8 }}>
          <input
            className="profile-editor__input"
            {...register(`intro.rotatingTitles.${i}`)}
            placeholder="e.g. Full Stack Engineer"
          />
          <button
            type="button"
            className="profile-editor__btn profile-editor__btn--danger"
            onClick={() => remove(i)}
          >
            Remove
          </button>
        </div>
      ))}
      <button
        type="button"
        className="profile-editor__btn profile-editor__btn--ghost"
        onClick={() => append("")}
      >
        + Add line
      </button>
    </div>
  );
}

async function fetchMyProfile() {
  const res = await fetch(`${apiBase}/profile/me`, {
    credentials: "include",
    headers: { "Content-Type": "application/json" },
  });
  const json = await res.json();
  if (res.status === 401) {
    throw Object.assign(new Error(json.message || "Unauthorized"), { status: 401 });
  }
  if (!res.ok || json.success === false) {
    throw new Error(json.message || "Failed to load profile");
  }
  return json.data;
}

function InfoIcon() {
  return (
    <svg className="profile-editor__callout-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
      <circle cx="12" cy="12" r="10" />
      <path d="M12 16v-4M12 8h.01" strokeLinecap="round" />
    </svg>
  );
}

function CollapseChevron() {
  return (
    <svg
      className="profile-editor__collapse-icon"
      width="20"
      height="20"
      viewBox="0 0 24 24"
      fill="currentColor"
      aria-hidden
    >
      <path d="M7.41 8.59L12 13.17l4.59-4.58L18 10l-6 6-6-6 1.41-1.41z" />
    </svg>
  );
}

/**
 * Top-level form blocks (Hero, Experience, Education).
 * Fields stay mounted when collapsed so the form state is unchanged.
 */
function EditorSectionCollapsible({ sectionId, title, children, defaultOpen = true }) {
  const [open, setOpen] = useState(defaultOpen);
  const panelId = `${sectionId}-panel`;
  const triggerId = `${sectionId}-trigger`;

  return (
    <section className="profile-editor__section" aria-labelledby={triggerId}>
      <h2 className="profile-editor__section-title profile-editor__section-title--collapsible">
        <button
          type="button"
          className="profile-editor__collapsible-trigger"
          id={triggerId}
          aria-expanded={open}
          aria-controls={panelId}
          onClick={() => setOpen((o) => !o)}
        >
          <span className={`profile-editor__collapse-icon-wrap ${open ? "is-open" : ""}`}>
            <CollapseChevron />
          </span>
          <span className="profile-editor__collapsible-trigger-text">{title}</span>
        </button>
      </h2>
      <div
        id={panelId}
        role="region"
        aria-labelledby={triggerId}
        hidden={!open}
        className="profile-editor__collapsible-panel"
      >
        {children}
      </div>
    </section>
  );
}

/**
 * Per-card collapse for experience / education entries (keyed by stable field id).
 */
function useCardCollapse(defaultOpen = true) {
  const [map, setMap] = useState({});
  const isOpen = (fieldId) => (map[fieldId] === undefined ? defaultOpen : map[fieldId]);
  const toggle = (fieldId) =>
    setMap((m) => {
      const cur = m[fieldId] === undefined ? defaultOpen : m[fieldId];
      return { ...m, [fieldId]: !cur };
    });
  return { isOpen, toggle };
}

function EditorCardCollapsible({
  fieldId,
  title,
  isOpen,
  onToggle,
  headerActions,
  children,
}) {
  const panelId = `card-panel-${fieldId}`;
  const triggerId = `card-trigger-${fieldId}`;

  return (
    <article className="profile-editor__card">
      <div className="profile-editor__card-head profile-editor__card-head--collapsible">
        <h3 className="profile-editor__card-title profile-editor__card-title--collapsible">
          <button
            type="button"
            className="profile-editor__card-toggle"
            id={triggerId}
            aria-expanded={isOpen}
            aria-controls={panelId}
            onClick={() => onToggle(fieldId)}
          >
            <span className={`profile-editor__collapse-icon-wrap ${isOpen ? "is-open" : ""}`}>
              <CollapseChevron />
            </span>
            <span className="profile-editor__card-toggle-title">{title}</span>
          </button>
        </h3>
        {headerActions}
      </div>
      <div
        id={panelId}
        role="region"
        aria-labelledby={triggerId}
        hidden={!isOpen}
        className="profile-editor__card-body"
      >
        {children}
      </div>
    </article>
  );
}

function ExpListItems({ nestIndex, control, register }) {
  const { fields, append, remove } = useFieldArray({
    control,
    name: `expDetails.${nestIndex}.listItems`,
  });

  return (
    <div className="profile-editor__bullets">
      <div className="profile-editor__bullets-label">Highlights &amp; responsibilities</div>
      {fields.map((field, i) => (
        <div key={field.id} className="profile-editor__bullet-row">
          <span className="profile-editor__bullet-num" aria-hidden>
            {i + 1}
          </span>
          <textarea
            className="profile-editor__textarea"
            {...register(`expDetails.${nestIndex}.listItems.${i}`)}
            rows={4}
            placeholder="Describe impact, tech stack, or achievements…"
          />
          <button
            type="button"
            className="profile-editor__btn profile-editor__btn--danger"
            onClick={() => remove(i)}
            title="Remove this bullet"
          >
            Remove
          </button>
        </div>
      ))}
      <div className="profile-editor__toolbar">
        <button
          type="button"
          className="profile-editor__btn profile-editor__btn--ghost"
          onClick={() => append("")}
        >
          + Add bullet
        </button>
      </div>
    </div>
  );
}

function ProfileEditor() {
  const queryClient = useQueryClient();
  const { refetch: refetchPublicProfile } = useProfile();

  const { data, isLoading, error, isError } = useQuery({
    queryKey: ["profile-me"],
    queryFn: fetchMyProfile,
    retry: false,
  });

  const {
    register,
    control,
    handleSubmit,
    reset,
    formState: { errors, isDirty },
  } = useForm({
    resolver: zodResolver(profileFormSchema),
    defaultValues: {
      intro: defaultIntroForm(),
      expDetails: [],
      eduDetails: [],
    },
  });

  const {
    fields: expFields,
    append: appendExp,
    remove: removeExp,
  } = useFieldArray({ control, name: "expDetails" });

  const {
    fields: eduFields,
    append: appendEdu,
    remove: removeEdu,
  } = useFieldArray({ control, name: "eduDetails" });

  const expCollapse = useCardCollapse(true);
  const eduCollapse = useCardCollapse(true);

  useEffect(() => {
    if (!data) return;
    reset(mapApiToForm(data));
  }, [data, reset]);

  const saveMutation = useMutation({
    mutationFn: async (formData) => {
      let rotatingTitles = (formData.intro.rotatingTitles || [])
        .map((t) => String(t).trim())
        .filter(Boolean);
      if (!rotatingTitles.length) {
        rotatingTitles = [...(fbIntro.rotatingTitles || ["Full Stack Engineer"])];
      }
      const res = await fetch(`${apiBase}/profile`, {
        method: "PUT",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          intro: {
            greeting: formData.intro.greeting,
            bio: formData.intro.bio || "",
            imageSrc: formData.intro.imageSrc || "images/me.jpg",
            rotatingTitles,
          },
          expDetails: formData.expDetails.map(({ id, ...rest }) => ({
            ...rest,
            ...(id ? { id } : {}),
          })),
          eduDetails: formData.eduDetails.map(({ id, ...rest }) => ({
            ...rest,
            ...(id ? { id } : {}),
          })),
        }),
      });
      const json = await res.json();
      if (!res.ok || json.success === false) {
        throw new Error(json.message || "Save failed");
      }
      return json.data;
    },
    onSuccess: async (saved) => {
      await queryClient.invalidateQueries({ queryKey: ["profile-me"] });
      await refetchPublicProfile();
      if (saved) reset(mapApiToForm(saved));
    },
  });

  const onSubmit = (formData) => {
    saveMutation.mutate(formData);
  };

  if (!apiBase) {
    return (
      <div className="profile-editor">
        <div className="profile-editor__inner">
          <div className="profile-editor__alert profile-editor__alert--warn">
            Set <code>REACT_APP_BACKEND_API_URL</code> in your environment to use the profile editor.
          </div>
        </div>
      </div>
    );
  }

  if (isLoading) return <Loader isLoading />;

  if (isError && error?.status === 401) {
    return (
      <div className="profile-editor">
        <div className="profile-editor__inner">
          <div className="profile-editor__alert profile-editor__alert--info">
            Please log in at <strong>/admin</strong> to edit your portfolio profile. Your session may have expired.
          </div>
        </div>
      </div>
    );
  }

  if (isError) {
    return (
      <div className="profile-editor">
        <div className="profile-editor__inner">
          <div className="profile-editor__alert profile-editor__alert--error">
            {error.message || "Could not load profile. Check that the API is running."}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="profile-editor">
      <div className="profile-editor__inner">
        <header className="profile-editor__hero">
          <h1>Portfolio profile</h1>
          <p>
            Edit what visitors see on your site: hero intro, work history, education, and highlights. Changes save to
            your database and update the public portfolio after you save.
          </p>
          <div className="profile-editor__badges" aria-label="Data storage">
            <span className="profile-editor__badge">MongoDB</span>
            <span className="profile-editor__badge">Live site</span>
          </div>
        </header>

        <div className="profile-editor__callout" role="note">
          <InfoIcon />
          <div>
            <strong>Images:</strong> use a full URL or a path served from{" "}
            <code>public/</code> (hero photo and logos). Profile image path is relative to <code>public/</code>, e.g.{" "}
            <code>images/me.jpg</code>.
          </div>
        </div>

        <form onSubmit={handleSubmit(onSubmit)} noValidate>
          <EditorSectionCollapsible sectionId="intro" title="Hero / intro">
            <article className="profile-editor__card profile-editor__card--nested">
              <div className="profile-editor__card-body">
                <div className="profile-editor__field">
                  <label className="profile-editor__label" htmlFor="intro-greeting">
                    Greeting line
                  </label>
                  <input
                    id="intro-greeting"
                    className="profile-editor__input"
                    {...register("intro.greeting")}
                    placeholder='e.g. Hi, I am Sunil Rawat'
                  />
                </div>
                <IntroRotatingLines control={control} register={register} />
                <div className="profile-editor__field">
                  <label className="profile-editor__label" htmlFor="intro-bio">
                    About paragraph
                  </label>
                  <textarea
                    id="intro-bio"
                    className="profile-editor__textarea"
                    rows={8}
                    {...register("intro.bio")}
                    placeholder="Long intro text shown under the headline."
                  />
                </div>
                <div className="profile-editor__field">
                  <label className="profile-editor__label" htmlFor="intro-img">
                    Profile image path or URL
                  </label>
                  <input
                    id="intro-img"
                    className="profile-editor__input"
                    {...register("intro.imageSrc")}
                    placeholder="images/me.jpg"
                  />
                </div>
              </div>
            </article>
          </EditorSectionCollapsible>

          <EditorSectionCollapsible sectionId="experience" title="Experience">
            {expFields.length === 0 && (
              <div className="profile-editor__empty">
                No roles yet. Add your first position to show it on the portfolio.
              </div>
            )}
            {expFields.map((field, index) => (
              <EditorCardCollapsible
                key={field.id}
                fieldId={field.id}
                title={`Position ${index + 1}`}
                isOpen={expCollapse.isOpen(field.id)}
                onToggle={expCollapse.toggle}
                headerActions={
                  <button
                    type="button"
                    className="profile-editor__btn profile-editor__btn--danger"
                    onClick={() => removeExp(index)}
                  >
                    Remove position
                  </button>
                }
              >
                <input type="hidden" {...register(`expDetails.${index}.id`)} />
                <div className="profile-editor__field">
                  <label className="profile-editor__label" htmlFor={`exp-title-${index}`}>
                    Company / headline
                  </label>
                  <input
                    id={`exp-title-${index}`}
                    className="profile-editor__input"
                    {...register(`expDetails.${index}.title`)}
                    autoComplete="organization"
                  />
                </div>
                <div className="profile-editor__field">
                  <label className="profile-editor__label" htmlFor={`exp-sub-${index}`}>
                    Role &amp; location
                  </label>
                  <input
                    id={`exp-sub-${index}`}
                    className="profile-editor__input"
                    {...register(`expDetails.${index}.subtitle`)}
                  />
                </div>
                <div className="profile-editor__field">
                  <label className="profile-editor__label" htmlFor={`exp-year-${index}`}>
                    Duration
                  </label>
                  <input
                    id={`exp-year-${index}`}
                    className="profile-editor__input"
                    {...register(`expDetails.${index}.year`)}
                    placeholder="e.g. Jan 2022 – Present"
                  />
                </div>
                <div className="profile-editor__field">
                  <label className="profile-editor__label" htmlFor={`exp-src-${index}`}>
                    Logo URL or path
                  </label>
                  <input
                    id={`exp-src-${index}`}
                    className="profile-editor__input"
                    {...register(`expDetails.${index}.src`)}
                    placeholder="/images/logo.jpeg"
                  />
                </div>
                <ExpListItems nestIndex={index} control={control} register={register} />
              </EditorCardCollapsible>
            ))}
            <div className="profile-editor__add-row">
              <button
                type="button"
                className="profile-editor__btn profile-editor__btn--accent"
                onClick={() =>
                  appendExp({
                    title: "",
                    subtitle: "",
                    year: "",
                    src: "",
                    listItems: [""],
                  })
                }
              >
                + Add experience
              </button>
            </div>
          </EditorSectionCollapsible>

          <EditorSectionCollapsible sectionId="education" title="Education">
            {eduFields.length === 0 && (
              <div className="profile-editor__empty">
                No schools listed. Add degrees or certifications to show them on the portfolio.
              </div>
            )}
            {eduFields.map((field, index) => (
              <EditorCardCollapsible
                key={field.id}
                fieldId={field.id}
                title={`School ${index + 1}`}
                isOpen={eduCollapse.isOpen(field.id)}
                onToggle={eduCollapse.toggle}
                headerActions={
                  <button
                    type="button"
                    className="profile-editor__btn profile-editor__btn--danger"
                    onClick={() => removeEdu(index)}
                  >
                    Remove
                  </button>
                }
              >
                <input type="hidden" {...register(`eduDetails.${index}.id`)} />
                <div className="profile-editor__field">
                  <label className="profile-editor__label" htmlFor={`edu-title-${index}`}>
                    Institution
                  </label>
                  <input
                    id={`edu-title-${index}`}
                    className="profile-editor__input"
                    {...register(`eduDetails.${index}.title`)}
                  />
                </div>
                <div className="profile-editor__field">
                  <label className="profile-editor__label" htmlFor={`edu-sub-${index}`}>
                    Degree / program
                  </label>
                  <input
                    id={`edu-sub-${index}`}
                    className="profile-editor__input"
                    {...register(`eduDetails.${index}.subtitle`)}
                  />
                </div>
                <div className="profile-editor__field">
                  <label className="profile-editor__label" htmlFor={`edu-year-${index}`}>
                    Years
                  </label>
                  <input
                    id={`edu-year-${index}`}
                    className="profile-editor__input"
                    {...register(`eduDetails.${index}.year`)}
                  />
                </div>
                <div className="profile-editor__field">
                  <label className="profile-editor__label" htmlFor={`edu-src-${index}`}>
                    Logo URL or path
                  </label>
                  <input
                    id={`edu-src-${index}`}
                    className="profile-editor__input"
                    {...register(`eduDetails.${index}.src`)}
                    placeholder="/images/university.jpeg"
                  />
                </div>
              </EditorCardCollapsible>
            ))}
            <div className="profile-editor__add-row">
              <button
                type="button"
                className="profile-editor__btn profile-editor__btn--accent"
                onClick={() =>
                  appendEdu({
                    title: "",
                    subtitle: "",
                    year: "",
                    src: "",
                  })
                }
              >
                + Add education
              </button>
            </div>
          </EditorSectionCollapsible>

          {(errors.intro || errors.expDetails || errors.eduDetails) && (
            <div className="profile-editor__alert profile-editor__alert--error" style={{ marginBottom: "1rem" }}>
              Please fix the highlighted fields and try again.
            </div>
          )}

          <div className="profile-editor__sticky-actions">
            <button
              type="submit"
              className="profile-editor__btn profile-editor__btn--primary"
              disabled={saveMutation.isPending}
            >
              {saveMutation.isPending ? "Saving…" : "Save to database"}
            </button>
            {isDirty && !saveMutation.isPending && (
              <span className="profile-editor__status profile-editor__status--muted">Unsaved changes</span>
            )}
            {saveMutation.isSuccess && !isDirty && (
              <span className="profile-editor__status profile-editor__status--ok" role="status">
                Saved — live portfolio updated
              </span>
            )}
            {saveMutation.isError && (
              <span className="profile-editor__status profile-editor__status--err" role="alert">
                {saveMutation.error.message}
              </span>
            )}
          </div>
        </form>
      </div>
    </div>
  );
}

export default ProfileEditor;
